import { Routemize_Constants_SubscriptionTier } from '../../api/models/Routemize_Constants_SubscriptionTier';

/**
 * Single source of truth for plan-gated features.
 *
 * Each feature key is referenced by:
 *   - `useFeatureAccess(feature)` for click-point gating
 *   - `FeatureGate` for inline UI gating
 *   - `useUpgradePrompt().showUpgrade({ feature })` for ad-hoc upgrade prompts
 *
 * Backend feature flags on `TenantSubscriptionDto` (hasWhiteLabel, hasCrmIntegrations, etc.)
 * are still authoritative for billing — this catalog mirrors that mapping for the UI so
 * we can show graceful fallbacks (locks + upgrade modals) before the API rejects a request.
 *
 * Marketing copy here is intentionally short — the UpgradeModal renders a rich tier card
 * around it.
 */
export type PlanTier = 'Solo' | 'Pro' | 'Scale';

export const TIER_ORDER: Record<PlanTier, number> = {
  Solo: 0,
  Pro: 1,
  Scale: 2,
};

/**
 * Customer-facing name for each tier. The tier *value* stays 'Solo' everywhere it is
 * an identifier — the API enum, the Chargebee plan ids, and every lookup key in this
 * file — because those are contracts we don't get to rename. Only the wording shown
 * to a customer differs, so render this and never the raw tier value.
 */
export const TIER_LABELS: Record<PlanTier, string> = {
  Solo: 'Starter',
  Pro: 'Pro',
  Scale: 'Scale',
};

/** Display label for a tier; empty string when the tier is unknown. */
export function tierLabel(tier: PlanTier | null | undefined): string {
  return tier ? TIER_LABELS[tier] ?? tier : '';
}

export interface FeatureDefinition {
  /** Lowest tier on which the feature is available. */
  requiredTier: PlanTier;
  /** Short label shown in upgrade prompts and locks. */
  label: string;
  /** One-sentence value proposition for the upgrade modal. */
  description: string;
  /** Optional supporting bullet list (3–5 items) shown in the upgrade modal. */
  bullets?: string[];
}

/**
 * Add new gated features here. Keep the key stable; refactoring callers is easier than
 * tracking down stringly-typed references.
 */
export const FEATURE_CATALOG: Record<string, FeatureDefinition> = {
  // ── Solo features (available on every paid tier) ──
  CrmIntegrations: {
    requiredTier: 'Solo',
    label: 'CRM Integrations',
    description: 'Two-way sync with CRMs like DripJobs to keep contacts, jobs and notes in lockstep.',
  },

  // ── Pro features ──
  SmartAssignment: {
    requiredTier: 'Pro',
    label: 'Smart Assignment',
    description: 'Automatically route incoming jobs to the right team member based on territory, availability and load.',
    bullets: [
      'Round-robin or weighted assignment',
      'Per-territory and per-event-type rules',
      'Fallback to manual assignment when no match',
    ],
  },
  CustomTerritories: {
    requiredTier: 'Pro',
    label: 'Custom Territories',
    description: 'Define service areas by zip code or by drawing on a map — not just a radius from your business.',
    bullets: [
      'Draw service areas on a map',
      'Group zip codes into named territories',
      'Per-territory team assignment',
    ],
  },
  FormPrequalification: {
    requiredTier: 'Pro',
    label: 'Form Pre-qualification',
    description: 'Filter and tag leads from your booking forms before they hit your inbox.',
    bullets: [
      'Lead source capture on every form',
      'Conditional logic on responses',
      'Automatic disqualification rules',
    ],
  },
  Chatbot: {
    requiredTier: 'Pro',
    label: 'AI Chatbot',
    description: 'Embed an AI booking assistant on your website that converts visitors into appointments 24/7.',
    bullets: [
      'Embeddable on any site',
      'Hands off to your booking flow',
      'Trained on your services',
    ],
  },
  SmartFill: {
    requiredTier: 'Pro',
    label: 'Smart Fill',
    description: 'Instantly fill schedule gaps with the best-matched cancelled or unscheduled jobs.',
    bullets: [
      'Ranked candidate suggestions',
      'Respects drive time and territory',
      'One-click reschedule',
    ],
  },
  AddStop: {
    requiredTier: 'Pro',
    label: 'Add a Stop',
    description: "Add a new stop directly into a gap on someone's route without disrupting other appointments.",
    bullets: [
      'Pick from existing contacts or create new',
      'Live drive-time calculation',
      'Repeat the stop across multiple days',
    ],
  },
  PlanStop: {
    requiredTier: 'Pro',
    label: 'Plan a Stop',
    description: 'Plan future stops for follow-up jobs without committing to a specific time slot yet.',
  },
  BulkRoute: {
    requiredTier: 'Pro',
    label: 'Bulk Route',
    description: 'Drop in a full day of stops at once and let Routemize optimize the order, mileage, and fit.',
    bullets: [
      'Auto-orders stops to minimize drive time',
      'Respects locked times, lunch, and existing appointments',
      'Saves all stops in one click',
    ],
  },
  AIEnhanceRoute: {
    requiredTier: 'Pro',
    label: 'AI Enhance Route',
    description: 'Let AI re-sequence the day to cut drive time and squeeze in extra stops where possible.',
    bullets: [
      'Re-orders stops for shortest drive',
      'Respects appointment windows',
      'Side-by-side preview before applying',
    ],
  },
  Zapier: {
    requiredTier: 'Pro',
    label: 'Zapier Integration',
    description: 'Connect Routemize to 6,000+ apps via Zapier triggers and actions.',
  },

  // ── Scale features ──
  VoiceAgent: {
    requiredTier: 'Scale',
    label: 'Voice Agent',
    description: 'An AI voice agent that answers your phone, qualifies leads and books appointments — even after hours.',
    bullets: [
      'Real phone number',
      'Books straight into your calendar',
      'Transcripts and call summaries',
    ],
  },
  WhiteLabel: {
    requiredTier: 'Scale',
    label: 'White Label',
    description: 'Remove Routemize branding from booking forms, emails and customer-facing pages.',
  },
  SameDayBookings: {
    requiredTier: 'Scale',
    label: 'Same-Day Bookings',
    description: 'Allow customers to book a slot for today, with intelligent buffer rules.',
  },
};

/**
 * String literal union of every catalog key. Kept loose (`string`) by design so callers
 * can pass user-supplied keys without TypeScript fighting them — `getFeatureDefinition`
 * gracefully handles unknown keys with a generic Pro fallback.
 */
export type FeatureKey =
  | 'SmartAssignment'
  | 'CustomTerritories'
  | 'FormPrequalification'
  | 'Chatbot'
  | 'SmartFill'
  | 'AddStop'
  | 'PlanStop'
  | 'BulkRoute'
  | 'AIEnhanceRoute'
  | 'Zapier'
  | 'VoiceAgent'
  | 'WhiteLabel'
  | 'CrmIntegrations'
  | 'SameDayBookings';

/** True when the catalog has an entry for the given key. */
export function isKnownFeature(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(FEATURE_CATALOG, key);
}

/** Look up a feature; falls back to a generic "Pro" definition for unknown keys so the UI never crashes. */
export function getFeatureDefinition(key: string): FeatureDefinition {
  if (isKnownFeature(key)) return FEATURE_CATALOG[key];
  return {
    requiredTier: 'Pro',
    label: key,
    description: 'Upgrade your plan to unlock this feature.',
  };
}

/** True if `currentTier` already includes everything in `requiredTier`. */
export function tierIncludes(currentTier: PlanTier | null | undefined, requiredTier: PlanTier): boolean {
  if (!currentTier) return false;
  return (TIER_ORDER[currentTier] ?? -1) >= TIER_ORDER[requiredTier];
}

/** Returns the next higher tier above `current`, or null if already at the top. */
export function nextTierAbove(current: PlanTier | null | undefined): PlanTier | null {
  const order: PlanTier[] = ['Solo', 'Pro', 'Scale'];
  if (!current) return 'Pro';
  const idx = order.indexOf(current);
  if (idx < 0 || idx >= order.length - 1) return null;
  return order[idx + 1];
}

/** Returns the next lower tier below `current`, or null if already at the bottom. */
export function nextTierBelow(current: PlanTier | null | undefined): PlanTier | null {
  const order: PlanTier[] = ['Solo', 'Pro', 'Scale'];
  if (!current) return null;
  const idx = order.indexOf(current);
  if (idx <= 0) return null;
  return order[idx - 1];
}

export const TIER_PRICING: Record<PlanTier, { monthlyPrice: number; annualPrice: number }> = {
  Solo: { monthlyPrice: 49, annualPrice: 39 },
  Pro: { monthlyPrice: 99, annualPrice: 79 },
  Scale: { monthlyPrice: 199, annualPrice: 159 },
};

/**
 * Map a tier string from the API (which may be the enum value 'Solo'/'Pro'/'Scale'
 * or a stale numeric string '0'/'1'/'2') to a normalized PlanTier.
 */
export function normalizeTier(raw: string | number | null | undefined): PlanTier | null {
  if (raw == null) return null;
  if (typeof raw === 'number') {
    if (raw === 0) return 'Solo';
    if (raw === 1) return 'Pro';
    if (raw === 2) return 'Scale';
    return null;
  }
  const trimmed = String(raw).trim();
  if (trimmed === Routemize_Constants_SubscriptionTier.SOLO) return 'Solo';
  if (trimmed === Routemize_Constants_SubscriptionTier.PRO) return 'Pro';
  if (trimmed === Routemize_Constants_SubscriptionTier.SCALE) return 'Scale';
  if (trimmed === '0') return 'Solo';
  if (trimmed === '1') return 'Pro';
  if (trimmed === '2') return 'Scale';
  return null;
}
